import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faFileCsv, faCheckCircle, faExclamationTriangle,
  faUser, faCode, faClock, faGlobe, faInfoCircle, faEye,
  faShieldAlt, faCalendarAlt, faNetworkWired, faSyncAlt
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

// ============================================================
// 1. CACHE DEXIE (24h) avec fallback périmé
// ============================================================
const db = new Dexie('HistoriqueCacheDB');
db.version(1).stores({
  actions: 'key, data, timestamp'
});
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedActions(key, allowExpired = false) {
  try {
    const entry = await db.actions.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch {
    return null;
  }
}
async function setCachedActions(key, data) {
  try {
    await db.actions.put({ key, data, timestamp: Date.now() });
  } catch { /* ignore */ }
}
async function getStaleActions(key) {
  return getCachedActions(key, true);
}

// ============================================================
// 2. DÉDOUBLONNAGE DES REQUÊTES EN COURS
// ============================================================
const pendingFetches = new Map();

// ============================================================
// 3. UTILITAIRE FETCH JSON SÉCURISÉ
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }
  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

// ============================================================
// 4. COMPOSANT PRINCIPAL
// ============================================================
const HistoriqueActions = () => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0, limit: 10 });
  const [dataSource, setDataSource] = useState('cache');
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);

  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const isMounted = useRef(true);

  // ============================================================
  // 5. GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((message, variant = 'success', duration = 5000) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, variant });
    toastTimeoutRef.current = setTimeout(() => {
      if (isMounted.current) setToast({ show: false });
      toastTimeoutRef.current = null;
    }, duration);
  }, []);

  // ============================================================
  // 6. RÉCUPÉRATION DES ACTIONS (avec cache, dédoublonnage, annulation)
  // ============================================================
  const fetchActions = useCallback(async (page = 1, search = '', forceRefresh = false, retryCount = 0) => {
    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = `page_${page}_search_${search}`;
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    // Dédoublonnage : si une requête identique est déjà en cours, on attend sa promesse
    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setActions(result.data);
          setPagination(result.pagination);
          setDataSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    // Limiter les tentatives pour éviter les boucles infinies
    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    // Définir l'état de chargement
    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    setDataSource('');

    const fetchPromise = (async () => {
      try {
        // 1. Cache si non forcé
        if (!forceRefresh) {
          const cached = await getCachedActions(cacheKey);
          if (cached) {
            if (isMounted.current) {
              setActions(cached.data);
              setPagination(cached.pagination);
              setDataSource('cache');
              setLoading(false);
              setRefreshing(false);
            }
            // Mise à jour en arrière‑plan (stale-while-revalidate)
            if (!forceRefresh && !refreshing) {
              // On déclenche une mise à jour silencieuse en arrière‑plan
              setTimeout(() => {
                if (isMounted.current) {
                  fetchActions(page, search, true);
                }
              }, 100);
            }
            return cached;
          }
        }

        // 2. Requête réseau
        const url = `${API_CONFIG2.apiUrl}paie/historique_api.php?action=fetch&page=${page}&limit=${pagination.limit}&search=${encodeURIComponent(search)}`;
        const response = await fetch(url, { signal: controller.signal });

        // 3. Gestion 429
        if (response.status === 429) {
          // Essayer le cache périmé
          const stale = await getStaleActions(cacheKey);
          if (stale) {
            if (isMounted.current) {
              setActions(stale.data);
              setPagination(stale.pagination);
              setDataSource('cache (périmé)');
              showToast('429 – Utilisation du cache (données peut-être obsolètes)', 'warning');
              setLoading(false);
              setRefreshing(false);
            }
            return stale;
          }
          // Sinon, backoff exponentiel
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          showToast(`Trop de requêtes. Nouvel essai dans ${waitTime/1000}s`, 'warning');
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchActions(page, search, forceRefresh, retryCount + 1);
        }

        // 4. Gestion des autres erreurs HTTP
        if (!response.ok) {
          const stale = await getStaleActions(cacheKey);
          if (stale) {
            if (isMounted.current) {
              setActions(stale.data);
              setPagination(stale.pagination);
              setDataSource('cache (périmé)');
              showToast('Erreur réseau – Utilisation du cache', 'warning');
              setLoading(false);
              setRefreshing(false);
            }
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        // 5. Lecture JSON
        const data = await response.json();
        if (data.status === 'success') {
          const result = { data: data.data, pagination: data.pagination };
          if (isMounted.current) {
            setActions(data.data);
            setPagination(data.pagination);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            await setCachedActions(cacheKey, result);
            if (forceRefresh) {
              showToast('Données actualisées', 'success');
            }
            setLoading(false);
            setRefreshing(false);
          }
          return result;
        } else {
          throw new Error(data.message || 'Erreur serveur');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        // Dernier recours : cache périmé
        const stale = await getStaleActions(cacheKey);
        if (stale && isMounted.current) {
          setActions(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          showToast('Erreur – Utilisation du cache', 'warning');
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        if (isMounted.current) {
          showToast(err.message || 'Erreur de chargement', 'danger');
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    // Stocker la promesse dans la Map pour dédoublonner
    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [pagination.limit, showToast]);

  // ============================================================
  // 7. GESTION DE LA RECHERCHE (debounce)
  // ============================================================
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchActions(1, val, false);
    }, 400);
  };

  // ============================================================
  // 8. CHARGEMENT INITIAL ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchActions(1, '', false);
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      pendingFetches.clear();
    };
  }, []);

  // ============================================================
  // 9. FORCER L'ACTUALISATION
  // ============================================================
  const handleForceRefresh = () => {
    if (!refreshing && !loading) {
      fetchActions(pagination.currentPage, searchTerm, true);
    }
  };

  // ============================================================
  // 10. EXPORT CSV
  // ============================================================
  const exportCSV = () => {
    if (!actions.length) {
      showToast('Aucune donnée à exporter', 'danger');
      return;
    }
    const headers = ['ID', 'Utilisateur', 'Type', 'Description', 'Détails', 'IP', 'User-Agent', 'Date'];
    const rows = actions.map(a => [
      a.id,
      a.utilisateur_id || '',
      a.action_type,
      a.description,
      a.details ? JSON.stringify(a.details) : '',
      a.adresse_ip,
      a.user_agent || '',
      a.date_action
    ]);
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `historique_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ============================================================
  // 11. MODALE DE DÉTAILS
  // ============================================================
  const handleRowClick = (action) => {
    setSelectedAction(action);
    setShowDetailModal(true);
  };

  // ============================================================
  // 12. UTILITAIRE POUR LES ICÔNES
  // ============================================================
  const getActionIcon = (type) => {
    const lower = type?.toLowerCase() || '';
    if (lower.includes('login') || lower.includes('connexion')) return { icon: faUser, color: '#3b82f6' };
    if (lower.includes('create') || lower.includes('ajout')) return { icon: faCheckCircle, color: '#3b82f6' };
    if (lower.includes('delete') || lower.includes('suppr')) return { icon: faExclamationTriangle, color: '#ef4444' };
    if (lower.includes('update') || lower.includes('modif')) return { icon: faCode, color: '#f59e0b' };
    return { icon: faInfoCircle, color: '#94a3b8' };
  };

  // ============================================================
  // 13. AFFICHAGE DU CHARGEMENT
  // ============================================================
  if (loading && !actions.length && !refreshing) {
    return (
      <div className="historique-page text-center py-5">
        <div className="spinner-border text-primary" role="status" />
        <p className="text-secondary mt-2">Chargement de l'historique...</p>
      </div>
    );
  }

  // ============================================================
  // 14. RENDU PRINCIPAL (styles responsifs)
  // ============================================================
  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – HISTORIQUE ACTIONS (mobile optimisé)
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .historique-page {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: 24px;
          padding: clamp(1rem, 1.5vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 24px;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .badge-source {
          font-size: clamp(0.6rem, 0.9vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
          display: inline-block;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .monetra-btn {
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 0.9vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.4;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.p-2 {
          padding: 6px 10px;
          min-width: 36px;
          justify-content: center;
        }

        .search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 16px;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 180px;
          max-width: 100%;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        .glass-table {
          width: 100%;
          border-collapse: collapse;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
        }
        .glass-table th {
          padding: 12px 10px;
          text-align: left;
          font-size: clamp(0.6rem, 0.8vw, 0.75rem);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.04);
          font-weight: 600;
        }
        .glass-table td {
          padding: 10px 10px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          vertical-align: middle;
        }
        .glass-table tbody tr {
          transition: all 0.2s cubic-bezier(0.2, 0.8, 0.2, 1);
          cursor: pointer;
          animation: fadeSlideUp 0.4s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
          opacity: 0;
          transform: translateY(10px);
        }
        .glass-table tbody tr:nth-child(1) { animation-delay: 0.05s; }
        .glass-table tbody tr:nth-child(2) { animation-delay: 0.10s; }
        .glass-table tbody tr:nth-child(3) { animation-delay: 0.15s; }
        .glass-table tbody tr:nth-child(4) { animation-delay: 0.20s; }
        .glass-table tbody tr:nth-child(5) { animation-delay: 0.25s; }
        .glass-table tbody tr:nth-child(6) { animation-delay: 0.30s; }
        .glass-table tbody tr:nth-child(7) { animation-delay: 0.35s; }
        .glass-table tbody tr:nth-child(8) { animation-delay: 0.40s; }
        .glass-table tbody tr:hover {
          background: rgba(255,255,255,0.05);
          transform: translateX(4px);
        }
        .glass-table tbody tr:active { transform: scale(0.98); }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .action-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: clamp(0.6rem, 0.8vw, 0.7rem);
          font-weight: 600;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
        }

        .monetra-modal .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
          padding: clamp(1rem, 1.5vw, 1.5rem) clamp(1.2rem, 2vw, 2rem);
        }
        .monetra-modal .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
          padding: clamp(0.8rem, 1.5vw, 1.2rem) clamp(1.2rem, 2vw, 2rem);
        }
        .monetra-modal .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }
        .monetra-modal .modal-body {
          padding: clamp(1.2rem, 2vw, 2rem);
        }

        .details-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: clamp(12px, 2vw, 16px);
          margin-bottom: 20px;
        }
        .details-item {
          background: rgba(255,255,255,0.02);
          border-radius: 14px;
          padding: 12px 16px;
          border: 1px solid rgba(255,255,255,0.04);
        }
        .details-item .label {
          color: var(--text-secondary);
          font-size: clamp(0.65rem, 0.8vw, 0.7rem);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }
        .details-item .value {
          font-weight: 500;
          font-size: clamp(0.85rem, 1vw, 0.95rem);
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .json-preview {
          background: rgba(0,0,0,0.4);
          border-radius: 14px;
          padding: 16px;
          border: 1px solid rgba(59, 130, 246, 0.08);
          margin-top: 10px;
        }
        .json-preview h6 {
          color: var(--text-secondary);
          font-size: clamp(0.7rem, 0.8vw, 0.75rem);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 8px;
        }
        .json-preview pre {
          margin: 0;
          font-family: 'Fira Code', monospace;
          font-size: clamp(0.8rem, 1vw, 0.85rem);
          white-space: pre-wrap;
          word-break: break-all;
          color: var(--text-primary);
        }

        .toast-monetra {
          background: rgba(10, 20, 45, 0.8) !important;
          backdrop-filter: blur(24px) !important;
          -webkit-backdrop-filter: blur(24px) !important;
          border: 1px solid rgba(59, 130, 246, 0.2) !important;
          border-radius: 16px !important;
          color: var(--text-primary) !important;
          box-shadow: 0 20px 50px rgba(0,0,0,0.6) !important;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }

        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-success { color: var(--accent-blue); }
        .text-danger { color: #ef4444; }
        .fw-bold { font-weight: 600; }
        .small { font-size: clamp(0.65rem, 0.8vw, 0.85rem); }
        .m-0 { margin: 0; }
        .mb-4 { margin-bottom: clamp(1rem, 2vw, 1.5rem); }
        .me-1 { margin-right: 0.25rem; }
        .me-2 { margin-right: 0.5rem; }
        .p-2 { padding: 0.5rem; }
        .p-3 { padding: 1rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .gap-2 { gap: 0.5rem; }
        .gap-3 { gap: 1rem; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .text-end { text-align: right; }
        .text-center { text-align: center; }
        .overflow-hidden { overflow: hidden; }
        .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .border-top { border-top: 1px solid rgba(255,255,255,0.04); }
        .border-opacity-10 { opacity: 0.1; }

        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-primary);
          opacity: 0.5;
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0 4px;
          margin-left: auto;
          transition: color 0.2s;
        }
        .btn-close-custom:hover { opacity: 1; }

        @media (max-width: 768px) {
          .historique-page { padding: 12px; }
          .glass-card { padding: 1rem; }
          .details-grid { grid-template-columns: 1fr; }
          .glass-table th, .glass-table td { font-size: 0.7rem; padding: 6px 8px; }
          .glass-table th:last-child, .glass-table td:last-child { min-width: 80px; }
          .monetra-btn { font-size: 0.75rem; padding: 6px 12px; min-height: 38px; flex: 1 0 auto; justify-content: center; }
          .monetra-btn.p-2 { padding: 4px 8px; min-width: 32px; }
          .d-flex.justify-content-between.align-items-center {
            flex-direction: column;
            align-items: stretch !important;
            gap: 12px;
          }
          .d-flex.justify-content-between.align-items-center > div:first-child { text-align: center; }
          .d-flex.gap-2 { flex-wrap: wrap; justify-content: center; }
          .search-wrapper { max-width: 100%; min-width: unset; }
          .monetra-modal .modal-footer { flex-direction: column-reverse; }
          .monetra-modal .modal-footer button { width: 100%; justify-content: center; }
        }
        @media (max-width: 480px) {
          .monetra-btn { font-size: 0.7rem; padding: 4px 10px; min-height: 34px; }
          .glass-table th, .glass-table td { font-size: 0.6rem; padding: 4px 6px; }
        }
      `}</style>

      <div className="historique-page">
        {/* Header avec badge source et boutons d'actualisation */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="fw-bold mb-1" style={{ fontSize: 'clamp(1.6rem, 4vw, 2.2rem)' }}>
              Historique des actions
            </h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              Consultez toutes les actions enregistrées.
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <span className={`badge-source ${dataSource === 'live' || dataSource === 'live (forcé)' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button
              className="monetra-btn"
              onClick={handleForceRefresh}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} />
              {refreshing ? 'Actualisation...' : 'Forcer'}
            </button>
            <button className="monetra-btn" onClick={exportCSV} disabled={actions.length === 0 || loading || refreshing}>
              <FontAwesomeIcon icon={faFileCsv} /> Exporter CSV
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="glass-card d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div className="search-wrapper" style={{ minWidth: '260px', flex: 1, maxWidth: '100%' }}>
            <FontAwesomeIcon icon={faSearch} className="text-secondary" />
            <input
              type="text"
              placeholder="Rechercher (type, description, IP...)"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="text-secondary small">
            <span>{pagination.totalRecords} enregistrements</span>
          </div>
        </div>

        {/* Table */}
        <div className="glass-card p-0 overflow-hidden mt-4">
          <div className="table-responsive">
            <table className="glass-table">
              <thead>
                <tr>
                  <th style={{ width: '80px' }}>ID</th>
                  <th>Action</th>
                  <th>Description</th>
                  <th style={{ width: '130px' }}>IP</th>
                  <th style={{ width: '170px' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {loading && !refreshing && actions.length === 0 ? (
                  <tr><td colSpan="5" className="text-center py-5"><div className="spinner-border text-primary" role="status" /></td></tr>
                ) : actions.length === 0 ? (
                  <tr><td colSpan="5" className="text-center py-5 text-secondary">Aucune action trouvée.</td></tr>
                ) : (
                  actions.map(item => {
                    const { icon, color } = getActionIcon(item.action_type);
                    return (
                      <tr key={item.id} onClick={() => handleRowClick(item)}>
                        <td><span className="fw-bold text-secondary">#{item.id}</span></td>
                        <td>
                          <span className="action-badge" style={{ color }}>
                            <FontAwesomeIcon icon={icon} />
                            {item.action_type}
                          </span>
                        </td>
                        <td>
                          <div className="text-secondary" style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.description}
                          </div>
                          <small className="text-secondary" style={{ fontSize: '0.7rem' }}>Utilisateur : {item.utilisateur_id || 'Inconnu'}</small>
                        </td>
                        <td><span className="text-secondary"><FontAwesomeIcon icon={faNetworkWired} className="me-1" />{item.adresse_ip}</span></td>
                        <td>
                          <span className="text-secondary" style={{ fontSize: '0.85rem' }}>
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                            {new Date(item.date_action).toLocaleString()}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-3 border-top border-secondary border-opacity-10 d-flex justify-content-between align-items-center flex-wrap">
            <span className="text-secondary small">Page {pagination.currentPage} sur {pagination.totalPages}</span>
            <div className="d-flex gap-2">
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => fetchActions(pagination.currentPage - 1, searchTerm, false)}
              >
                ‹
              </button>
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => fetchActions(pagination.currentPage + 1, searchTerm, false)}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MODALE DE DÉTAILS PREMIUM */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        centered
        className="monetra-modal"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold" style={{ fontSize: 'clamp(1.1rem, 2vw, 1.3rem)' }}>
            <FontAwesomeIcon icon={faInfoCircle} className="text-success me-2" />
            Détails de l'action #{selectedAction?.id}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAction && (
            <>
              <div className="details-grid">
                <div className="details-item">
                  <div className="label"><FontAwesomeIcon icon={faUser} className="me-1" /> Utilisateur</div>
                  <div className="value">{selectedAction.utilisateur_id || 'Système / Inconnu'}</div>
                </div>
                <div className="details-item">
                  <div className="label"><FontAwesomeIcon icon={faCode} className="me-1" /> Type d'action</div>
                  <div className="value">
                    <span className="action-badge" style={{ color: getActionIcon(selectedAction.action_type).color }}>
                      <FontAwesomeIcon icon={getActionIcon(selectedAction.action_type).icon} className="me-1" />
                      {selectedAction.action_type}
                    </span>
                  </div>
                </div>
                <div className="details-item" style={{ gridColumn: 'span 2' }}>
                  <div className="label"><FontAwesomeIcon icon={faInfoCircle} className="me-1" /> Description</div>
                  <div className="value">{selectedAction.description}</div>
                </div>
                <div className="details-item">
                  <div className="label"><FontAwesomeIcon icon={faGlobe} className="me-1" /> Adresse IP</div>
                  <div className="value"><FontAwesomeIcon icon={faNetworkWired} className="me-1 text-secondary" /> {selectedAction.adresse_ip}</div>
                </div>
                <div className="details-item">
                  <div className="label"><FontAwesomeIcon icon={faClock} className="me-1" /> Date & Heure</div>
                  <div className="value"><FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-secondary" /> {new Date(selectedAction.date_action).toLocaleString()}</div>
                </div>
              </div>

              {/* User-Agent */}
              <div className="details-item mb-3">
                <div className="label"><FontAwesomeIcon icon={faShieldAlt} className="me-1" /> User-Agent</div>
                <div className="value" style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  {selectedAction.user_agent || 'Non renseigné'}
                </div>
              </div>

              {/* Détails JSON */}
              {selectedAction.details && (
                <div className="json-preview">
                  <h6><FontAwesomeIcon icon={faCode} className="me-1" /> Données brutes (JSON)</h6>
                  <pre>{JSON.stringify(selectedAction.details, null, 2)}</pre>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDetailModal(false)} className="monetra-btn outline-secondary">Fermer</Button>
        </Modal.Footer>
      </Modal>

      {/* Toast de notification */}
      <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
        <div className={`toast-monetra p-3 ${toast.show ? 'd-block' : 'd-none'}`} style={{ minWidth: 'clamp(200px, 30vw, 300px)' }}>
          <div className="d-flex align-items-center gap-3">
            <FontAwesomeIcon
              icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
              className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
            />
            <span style={{ fontSize: 'clamp(0.85rem, 1.2vw, 1rem)' }}>{toast.message}</span>
            <button
              className="btn-close-custom ms-auto"
              onClick={() => setToast({ ...toast, show: false })}
              style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', opacity: 0.5, fontSize: '1.2rem' }}
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default HistoriqueActions;